/**
 * Animated horizontal process flow connector.
 * Renders a dotted-line track with animated travelling dots connecting N steps.
 * Place between the numbered steps in "How It Works" sections.
 */
export function ProcessFlow({
  steps = 3,
  className = "",
}: {
  steps?: number;
  className?: string;
}) {
  const W = 600;
  const H = 40;
  const gap = W / (steps - 1);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={`w-full pointer-events-none ${className}`}
      style={{ maxHeight: H }}
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          .pf-dot {
            animation: pf-travel 3s linear infinite;
          }
          .pf-dot:nth-child(2) { animation-delay: -1s; }
          .pf-dot:nth-child(3) { animation-delay: -2s; }
          @keyframes pf-travel {
            0%   { transform: translateX(0); opacity: 0; }
            5%   { opacity: 1; }
            95%  { opacity: 1; }
            100% { transform: translateX(${W}px); opacity: 0; }
          }
        }
      `}</style>

      {/* Dashed track */}
      <line
        x1="0" y1={H / 2}
        x2={W} y2={H / 2}
        stroke="oklch(0.085 0.004 50)"
        strokeWidth="0.7"
        strokeDasharray="4 6"
        strokeOpacity="0.22"
      />

      {/* Node circles at each step */}
      {Array.from({ length: steps }).map((_, i) => (
        <g key={i}>
          <circle
            cx={i * gap}
            cy={H / 2}
            r="5"
            fill="oklch(0.97 0.005 72)"
            stroke="oklch(0.45 0.08 213)"
            strokeWidth="1"
            strokeOpacity="0.55"
          />
          <circle
            cx={i * gap}
            cy={H / 2}
            r="2"
            fill="oklch(0.45 0.08 213)"
            opacity="0.5"
          />
        </g>
      ))}

      {/* Travelling dots */}
      {[0, 1, 2].map((i) => (
        <circle
          key={i}
          className="pf-dot"
          cx="0"
          cy={H / 2}
          r="2.5"
          fill="oklch(0.45 0.08 213)"
          opacity="0.65"
        />
      ))}
    </svg>
  );
}
